import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useApi } from '../../lib/api';
import ReasonModal from '../../components/shared/ReasonModal';
import Pagination from '../../components/shared/Pagination';

const PAGE_SIZE = 10;

const STATUS_LABELS = {
  unlinked: 'Waiting for signup',
  pending: 'Pending approval',
  active: 'Active',
  declined: 'Declined',
  removed: 'Removed by client',
  revoked: 'Revoked',
};

const STATUS_PILL = {
  unlinked: 's-grey',
  pending: 's-amber',
  active: 's-green',
  declined: 's-pink',
  removed: 's-purple',
  revoked: 's-red',
};

const TABS = [
  { key: 'all', label: 'All clients' },
  { key: 'active', label: 'Active', statuses: ['active'] },
  { key: 'invited', label: 'Invited', statuses: ['unlinked', 'pending'] },
  { key: 'removed', label: 'Removed', statuses: ['declined', 'removed', 'revoked'] },
];

const AV_COLORS = ['#8F9BAD', '#5B7FDE', '#3FAE9A', '#C77B3C', '#7C5CFF'];

function initials(name, email) {
  const source = (name || '').trim() || (email || '').trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

function avatarColor(seed) {
  let h = 0;
  for (const ch of seed || '') h = (h * 31 + ch.charCodeAt(0)) % 997;
  return AV_COLORS[h % AV_COLORS.length];
}

function rupees(paise) {
  return `₹${((paise || 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function daysAgo(iso) {
  const d = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 86400000));
  if (d === 0) return 'Today';
  if (d === 1) return '1 day';
  return `${d} days`;
}

function AddClientModal({ onAdd, onClose, adding, error }) {
  const [email, setEmail] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');

  const submit = (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedName = clientName.trim();
    if (!trimmedEmail || !trimmedName) return;
    onAdd({ email: trimmedEmail, clientName: trimmedName, clientPhone: clientPhone.trim() || undefined });
  };

  return (
    <div className="modal-backdrop" onClick={() => !adding && onClose()}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2>Add Client</h2>
          <button className="modal-close" onClick={onClose} disabled={adding}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="admin-banner error">{error}</div>}
          <form onSubmit={submit}>
            <div className="form-row">
              <label>Client name <em>*</em></label>
              <input className="form-input" placeholder="e.g. Acme Corp" value={clientName} onChange={(e) => setClientName(e.target.value)} maxLength={200} autoFocus />
            </div>
            <div className="form-row">
              <label>Email <em>*</em></label>
              <input className="form-input" type="email" placeholder="client@example.com" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} />
            </div>
            <div className="form-row">
              <label>Phone</label>
              <input
                className="form-input"
                type="tel"
                inputMode="tel"
                placeholder="Optional — e.g. +91…"
                value={clientPhone}
                onChange={(e) => setClientPhone(e.target.value.replace(/[^\d+\s-]/g, '').slice(0, 15))}
                maxLength={15}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <button type="button" className="btn-secondary" onClick={onClose} disabled={adding}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={adding || !email.trim() || !clientName.trim()}>
                {adding ? 'Adding…' : 'Add Client'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function PartnerClients() {
  const api = useApi();
  const { partner, refreshPartner } = useOutletContext() || {};
  const suspended = partner?.status === 'suspended';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [addError, setAddError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [revokeId, setRevokeId] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [tab, setTab] = useState('all');
  const [page, setPage] = useState(1);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await api.partnerListClients();
      setItems(res.items || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleAdd = async (payload) => {
    setAdding(true);
    setAddError(null);
    try {
      await api.partnerAddClient(payload);
      setFormOpen(false);
      refresh();
      refreshPartner?.();
    } catch (err) {
      setAddError(err.response?.data?.error || err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleRevoke = async (reason) => {
    setRevoking(true);
    try {
      await api.partnerRevokeClient(revokeId, reason);
      setRevokeId(null);
      refresh();
      refreshPartner?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setRevoking(false);
    }
  };

  const activeTab = TABS.find((t) => t.key === tab) || TABS[0];
  const filtered = activeTab.statuses ? items.filter((it) => activeTab.statuses.includes(it.status)) : items;
  const pagedItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <header className="bar">
        <div>
          <h1>Clients</h1>
          <p>Everyone linked to your partner account and what they have earned you.</p>
        </div>
        <div className="bar-side">
          <button className="btn" onClick={() => setFormOpen(true)} disabled={suspended} title={suspended ? 'Unavailable while your account is suspended' : undefined}>+ Add Client</button>
        </div>
      </header>

      <section className="view">
        <div className="tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`tab${tab === t.key ? ' on' : ''}`}
              onClick={() => { setTab(t.key); setPage(1); }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {error && <div className="admin-banner error">{error}</div>}

        {loading ? (
          <p className="muted">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="panel empty">
            <b>No clients here yet</b>
            <p>{items.length === 0 ? 'Add your first client to start earning commission.' : 'Nothing in this tab right now.'}</p>
          </div>
        ) : (
          <>
            <div className="panel tbl-scroll">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Status</th>
                    <th>Commission earned</th>
                    <th>Linked since</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedItems.map((it) => (
                    <tr key={it.id}>
                      <td>
                        <div className="who-cell">
                          <span className="av" style={{ background: avatarColor(it.client_email) }}>{initials(it.client_name, it.client_email)}</span>
                          <div>
                            <b style={{ overflowWrap: 'anywhere' }}>{it.client_name || it.client_email}</b>
                            <div className="sub">{it.client_email}</div>
                          </div>
                        </div>
                      </td>
                      <td><span className={`state ${STATUS_PILL[it.status] || 's-grey'}`}><i></i>{STATUS_LABELS[it.status] || it.status}</span></td>
                      <td>
                        {it.total_commission_paise > 0 ? (
                          <>
                            <span className="money">{rupees(it.total_commission_paise)}</span>
                            <div className="sub">{it.payment_count} payment{it.payment_count === 1 ? '' : 's'}</div>
                          </>
                        ) : (
                          <span className="muted">No payments yet</span>
                        )}
                      </td>
                      <td>
                        {new Date(it.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        <div className="sub">{daysAgo(it.created_at)}</div>
                      </td>
                      <td>
                        {['unlinked', 'pending', 'active'].includes(it.status) && (
                          <button className="act warn" onClick={() => setRevokeId(it.id)} disabled={suspended}>Revoke</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalItems={filtered.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
          </>
        )}

        <div className="note">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M12 16v-5M12 8h.01"/></svg>
          <span>When a client removes you, commission you already earned stays yours. You just stop earning on their future payments.</span>
        </div>
      </section>

      {formOpen && (
        <AddClientModal
          onAdd={handleAdd}
          onClose={() => { setFormOpen(false); setAddError(null); }}
          adding={adding}
          error={addError}
        />
      )}

      {revokeId != null && (
        <ReasonModal
          title="Revoke Client"
          label="Reason for revoking"
          confirmLabel="Confirm Revoke"
          busy={revoking}
          onConfirm={handleRevoke}
          onClose={() => setRevokeId(null)}
        />
      )}
    </>
  );
}
